import { ApiError, HttpErrorResponse } from '../../Types';
import { ErrorFactory } from './ErrorFactory';
import type { IHttpService, IWindowService, auto, IHttpResponse, IRequestShortcutConfig } from 'angular';
import type { ConfigService } from './mx-config-service';
import type { EventService } from './event-service';
import type { NotificationService } from './mx-notification-service';
import { EventCodes } from '../model/event-codes';
import { HttpResponse } from '../../Types/HttpResponse';

type QueryStringParamsType = Record<string, string | number | boolean | null | undefined>;

type ResponseType<T, ResponseFlag> = ResponseFlag extends true ? IHttpResponse<T> : ResponseFlag extends false ? T : never;

const httpHostServices: Record<string, HttpService> = {};

export class HttpService {
	public static readonly $inject = ['$http', 'mx.shell.Config', 'mx.shell.EventService', '$window', 'mx.shell.NotificationService', '$injector', 'mx.app.ErrorFactory'];
	private readonly restHosts;
	public baseUrl: string;

	private requestCount = 0;
	private counterTimeout?: NodeJS.Timeout;
	private readonly requestWaiters: Array<() => void> = [];
	private readonly requestIdleTimeout = 30000; // 30 seconds

	constructor(
		private readonly $http: IHttpService,
		private readonly configService: ConfigService,
		private readonly eventService: EventService,
		private readonly $window: IWindowService,
		private readonly notificationService: NotificationService,
		private readonly $injector: auto.IInjectorService,
		private readonly errorFactory: ErrorFactory
	) {
		this.restHosts = this.configService.get('restHosts');
		this.baseUrl = this.prepareBaseUrl(this.restHosts.default);
	}

	public host(restHostKey?: string, url?: string) {
		if (!restHostKey && !url) {
			return this;
		}

		const baseUrl = this.prepareBaseUrl((restHostKey && this.restHosts[restHostKey]) || url || restHostKey);
		if (httpHostServices[baseUrl]) {
			return httpHostServices[baseUrl];
		}

		const host = this.$injector.instantiate(HttpService);
		host.baseUrl = baseUrl;
		httpHostServices[baseUrl] = host;
		return host;
	}

	public async get<DataType, ResponseFlag extends boolean = false>(
		url: string,
		queryStringParams?: QueryStringParamsType,
		requestParams: IRequestShortcutConfig = {},
		skipNotification = false,
		resultAsWholeResponse?: ResponseFlag
	) {
		const request = async () => this.$http.get<DataType>(this.prepareUrl(url, queryStringParams), requestParams);
		return (await this.makeRequest<DataType>(request, skipNotification, resultAsWholeResponse)) as ResponseType<DataType, ResponseFlag>;
	}

	public async post<DataType, ResponseFlag extends boolean = false>(
		url: string,
		data?: unknown,
		requestParams?: IRequestShortcutConfig,
		queryStringParams?: QueryStringParamsType,
		skipNotification = false,
		resultAsWholeResponse?: ResponseFlag
	) {
		const request = async () => this.$http.post<DataType>(this.prepareUrl(url, queryStringParams), data, requestParams);
		return (await this.makeRequest<DataType>(request, skipNotification, resultAsWholeResponse)) as ResponseType<DataType, ResponseFlag>;
	}

	public async patch<DataType, ResponseFlag extends boolean = false>(
		url: string,
		data?: unknown,
		requestParams?: IRequestShortcutConfig,
		queryStringParams?: QueryStringParamsType,
		skipNotification = false,
		resultAsWholeResponse?: ResponseFlag
	) {
		const request = async () => this.$http.patch<DataType>(this.prepareUrl(url, queryStringParams), data, requestParams);
		return (await this.makeRequest<DataType>(request, skipNotification, resultAsWholeResponse)) as ResponseType<DataType, ResponseFlag>;
	}

	public async put<DataType, ResponseFlag extends boolean = false>(
		url: string,
		data?: unknown,
		requestParams?: IRequestShortcutConfig,
		queryStringParams?: QueryStringParamsType,
		skipNotification = false,
		resultAsWholeResponse?: ResponseFlag
	) {
		const request = async () => this.$http.put<DataType>(this.prepareUrl(url, queryStringParams), data, requestParams);
		return (await this.makeRequest<DataType>(request, skipNotification, resultAsWholeResponse)) as ResponseType<DataType, ResponseFlag>;
	}

	public async upload<DataType, ResponseFlag extends boolean = false>(
		url: string,
		file?: unknown,
		requestParams?: IRequestShortcutConfig,
		queryStringParams?: QueryStringParamsType,
		skipNotification = false,
		resultAsWholeResponse?: ResponseFlag
	) {
		const extendedRequestParams = Object.assign({ headers: { 'Content-Type': 'application/octet-stream' }, transformRequest: [] }, requestParams || {});
		const request = async () => this.$http.post<DataType>(this.prepareUrl(url, queryStringParams), file, extendedRequestParams);
		return (await this.makeRequest<DataType>(request, skipNotification, resultAsWholeResponse)) as ResponseType<DataType, ResponseFlag>;
	}

	public async remove<DataType, ResponseFlag extends boolean = false>(
		url: string,
		data?: unknown,
		requestParams?: IRequestShortcutConfig,
		queryStringParams?: QueryStringParamsType,
		skipNotification = false,
		resultAsWholeResponse?: ResponseFlag
	) {
		if (data) {
			requestParams = requestParams || {};
			requestParams.data = data;
		}
		const request = async () => this.$http.delete<DataType>(this.prepareUrl(url, queryStringParams), requestParams);
		return (await this.makeRequest<DataType>(request, skipNotification, resultAsWholeResponse)) as ResponseType<DataType, ResponseFlag>;
	}

	public formatUrl(url: string): string {
		return this.prepareUrl(url);
	}

	public fixRelationLink(link = ''): string {
		return link.startsWith('http') ? link : this.$window.location.origin + this.ensureSlashPrefixed(link);
	}

	private prepareBaseUrl(url?: string): string {
		return this.removeTrailingSlash(this.fixRelationLink(url));
	}

	public prepareUrl(url: string, queryStringParams?: QueryStringParamsType) {
		const preparedUrl = this.addQueryStringParamsToUrl(this.encodeUrl(url), queryStringParams);
		return this.baseUrl + this.ensureSlashPrefixed(preparedUrl);
	}

	private removeTrailingSlash(source: string): string {
		return source.endsWith('/') ? source.slice(0, -1) : source;
	}

	private ensureSlashPrefixed(source: string): string {
		return source.startsWith('/') ? source : `/${source}`;
	}

	private encodeUrl(url: string): string {
		return url.replace(/([^?&=]+)=([^?&=]+)/g, (match, g1, g2) => {
			return g1 + '=' + (this.isEncoded(g2) ? g2 : encodeURIComponent(g2));
		});
	}

	private addQueryStringParamsToUrl(url: string, queryStringParams?: QueryStringParamsType) {
		let resultUrl = url;
		if (typeof queryStringParams === 'object') {
			const paramsString = this.toQueryString(queryStringParams);
			if (paramsString) {
				resultUrl += '/?' + paramsString;
			}
		}
		return resultUrl;
	}

	private async makeRequest<T>(request: () => Promise<IHttpResponse<T>>, skipNotification = false, resultAsWholeResponse?: boolean): Promise<HttpResponse<T> | T> {
		try {
			this._changeRequestCount(1);
			const response = await request();
			return resultAsWholeResponse ? response : response.data;
		} catch (_errorResponse) {
			const errorResponse = _errorResponse as HttpResponse<unknown>;

			if (errorResponse.config.responseType === 'arraybuffer' && errorResponse.data) {
				errorResponse.data = this.getDataForErrorArrayBuffer(errorResponse as HttpResponse<ArrayBufferLike>);
			}

			const error = this.errorFactory.createApiError(errorResponse as HttpErrorResponse);

			if (!skipNotification) {
				this.handleError(error);
			}

			throw error;
		} finally {
			//TODO: check that this is happened
			this._changeRequestCount(-1);
		}
	}

	private getDataForErrorArrayBuffer(errorResponse: HttpResponse<ArrayBufferLike>) {
		if ('TextDecoder' in window) {
			// Decode as UTF-8
			const dataView = new DataView(errorResponse.data);
			const decoder = new TextDecoder('utf8');
			return JSON.parse(decoder.decode(dataView));
		} else {
			// Fallback decode as ASCII
			const decodedString = String.fromCharCode.apply(null, Array.from(new Uint8Array(errorResponse.data)));
			return JSON.parse(decodedString);
		}
	}

	private isEncoded(uri: string): boolean {
		uri = uri || '';

		try {
			return uri !== decodeURIComponent(uri);
		} catch (e) {
			return false;
		}
	}

	private toQueryString(obj: QueryStringParamsType): string {
		const parts: string[] = [];

		for (const i in obj) {
			const value = obj[i];
			if (Object.prototype.hasOwnProperty.call(obj, i) && typeof value !== 'undefined' && value !== null) {
				parts.push(encodeURIComponent(i) + '=' + encodeURIComponent(value));
			}
		}
		return parts.join('&');
	}

	private handleError(error: ApiError) {
		if (error.title && error.message) {
			console.error(error.message, error.title);
			this.notificationService.error(error.message, error.title);
		}
	}

	private _changeRequestCount(amount: number) {
		this.requestCount += amount;
		if (this.requestCount === 0) {
			this.requestWaiters.forEach((waiter) => waiter());
			if (this.counterTimeout) {
				clearTimeout(this.counterTimeout);
			}
			this.counterTimeout = setTimeout(() => {
				if (this.requestCount === 0) {
					this.eventService.dispatchEvent(EventCodes.AllAjaxComplete);
				}
			}, 2000);
		}
	}

	public async waitForAllRequests(maxWaitForNextRequests: number = 0, stopWaitingTimeout: number = this.requestIdleTimeout): Promise<void> {
		const { promise, resolve } = Promise.withResolvers<void>();

		const waiter = $.debounce(maxWaitForNextRequests, () => {
			if (this.requestCount === 0) {
				clearTimeout(stopWaitingTimer);
				if (this.requestWaiters.includes(waiter)) {
					this.requestWaiters.splice(this.requestWaiters.indexOf(waiter), 1);
				}
				resolve();
			}
		});
		this.requestWaiters.push(waiter);

		const stopWaitingTimer = setTimeout(() => {
			if (this.requestWaiters.includes(waiter)) {
				this.requestWaiters.splice(this.requestWaiters.indexOf(waiter), 1);
				resolve();
			}
		}, stopWaitingTimeout);

		if (this.requestCount === 0) {
			waiter();
		}

		return promise;
	}
}

httpServiceFactory.$inject = ['$injector'];

export function httpServiceFactory($injector: auto.IInjectorService) {
	const host = $injector.instantiate(HttpService);
	httpHostServices[host.baseUrl] = host;
	return httpHostServices[host.baseUrl];
}
